import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert,
  Badge,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Assignment as AssignmentIcon,
  Photo as PhotoIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  ZoomIn as ZoomInIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import conditionReportService from '../services/conditionReportService';
import { SPECIAL_NOTES, SPECIAL_REPORT_TYPES } from '../constants/conditionReports';

const ConditionReportsSection = ({ reservationId }) => {
  const { t } = useTranslation('dashboard');
  const [conditionReports, setConditionReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [carouselIndices, setCarouselIndices] = useState({});

  // Composant Carrousel personnalisé
  const PhotoCarousel = ({ photos, reportId }) => {
    const [currentIndex, setCurrentIndex] = useState(carouselIndices[reportId] || 0);

    const handleNext = () => {
      const newIndex = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
      setCurrentIndex(newIndex);
      setCarouselIndices(prev => ({...prev, [reportId]: newIndex}));
    };

    const handlePrevious = () => {
      const newIndex = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
      setCurrentIndex(newIndex);
      setCarouselIndices(prev => ({...prev, [reportId]: newIndex}));
    };

    const handleThumbnailClick = (index) => {
      setCurrentIndex(index);
      setCarouselIndices(prev => ({...prev, [reportId]: index}));
    };

    if (!photos || photos.length === 0) return null;

    const currentPhoto = photos[currentIndex];

    return (
      <Box sx={{ position: 'relative', width: '100%' }}>
        {/* Image principale */}
        <Box 
          sx={{ 
            position: 'relative', 
            width: '100%', 
            height: 300,
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: 'grey.100'
          }}
        >
          <img
            src={currentPhoto.photo_url}
            alt={currentPhoto.description || `Photo ${currentPhoto.id}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          
          {/* Overlay avec informations */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0) 100%)',
              color: 'white',
              p: 2
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {currentPhoto.photo_type}
            </Typography>
            {currentPhoto.description && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {currentPhoto.description}
              </Typography>
            )}
          </Box>

          {/* Bouton zoom */}
          <IconButton
            onClick={() => handlePhotoClick(currentPhoto)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              }
            }}
          >
            <ZoomInIcon />
          </IconButton>

          {/* Navigation arrows (seulement si plus d'une photo) */}
          {photos.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)',
                  }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)',
                  }
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
            </>
          )}
        </Box>

        {/* Compteur de photos */}
        {photos.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem'
            }}
          >
            {currentIndex + 1} / {photos.length}
          </Box>
        )}

        {/* Thumbnails (si plus de 1 photo) */}
        {photos.length > 1 && (
          <Stack 
            direction="row" 
            spacing={1} 
            sx={{ 
              mt: 2, 
              overflowX: 'auto',
              pb: 1,
              '&::-webkit-scrollbar': {
                height: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '2px',
              }
            }}
          >
            {photos.map((photo, index) => (
              <Box
                key={photo.id}
                onClick={() => handleThumbnailClick(index)}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: currentIndex === index ? '2px solid' : '2px solid transparent',
                  borderColor: currentIndex === index ? 'primary.main' : 'transparent',
                  flexShrink: 0,
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  }
                }}
              >
                <img
                  src={photo.photo_url}
                  alt={`Thumbnail ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (reservationId) {
      fetchConditionReports();
    }
  }, [reservationId]);

  const fetchConditionReports = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await conditionReportService.getByReservation(reservationId);
      setConditionReports(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des condition reports:', error);
      setError(t('conditionReports.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'success';
      case 'refused':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <CheckCircleIcon />;
      case 'refused':
        return <CancelIcon />;
      case 'pending':
        return <WarningIcon />;
      default:
        return <AssignmentIcon />;
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      pickup: t('conditionReports.types.pickup'),
      return: t('conditionReports.types.return'),
      damage_report: t('conditionReports.types.damageReport'),
      maintenance: t('conditionReports.types.maintenance'),
      pickup_refusal: t('conditionReports.types.pickup_refusal'),
      return_refusal: t('conditionReports.types.return_refusal')
    };
    return types[type] || type;
  };

  const getTranslatedTypeLabel = (type) => {
    if (!type) return '';
    
    // Utiliser les constantes pour tous les types et les traduire
    switch (type) {
      case SPECIAL_REPORT_TYPES.PICKUP:
        return t('conditionReports.types.pickup');
      case SPECIAL_REPORT_TYPES.RETURN:
        return t('conditionReports.types.return');
      case SPECIAL_REPORT_TYPES.PICKUP_REFUSAL:
        return t('conditionReports.types.pickup_refusal');
      case SPECIAL_REPORT_TYPES.RETURN_REFUSAL:
        return t('conditionReports.types.return_refusal');
      case SPECIAL_REPORT_TYPES.DAMAGE_REPORT:
        return t('conditionReports.types.damageReport');
      case SPECIAL_REPORT_TYPES.MAINTENANCE:
        return t('conditionReports.types.maintenance');
      default:
        return getTypeLabel(type); // Fallback pour d'autres types non prévus
    }
  };

  const getUserRoleLabel = (role) => {
    const roles = {
      client: t('conditionReports.roles.client'),
      employee: t('conditionReports.roles.employee'),
      admin: t('conditionReports.roles.admin')
    };
    return roles[role] || role;
  };

  const getTranslatedNote = (note) => {
    if (!note) return '';
    
    // Vérifier si c'est une note spéciale et la traduire
    switch (note) {
      case SPECIAL_NOTES.RETURN_CONFIRMED:
        return t('conditionReports.notes.returnConfirmed');
      case SPECIAL_NOTES.REFUSAL:
        return t('conditionReports.notes.refusal');
      case SPECIAL_NOTES.PICKUP_CONFIRMED:
        return t('conditionReports.notes.pickupConfirmed');
      default:
        return note; // Retourner la note originale si ce n'est pas une note spéciale
    }
  };

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
  };

  const handleClosePhotoDialog = () => {
    setSelectedPhoto(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {t('conditionReports.messages.loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (conditionReports.length === 0) {
    return (
      <Box textAlign="center" p={3}>
        <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h6" color="text.secondary">
          {t('conditionReports.messages.noReports')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('conditionReports.messages.reportsWillAppear')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AssignmentIcon sx={{ mr: 1 }} />
        {t('conditionReports.title', { count: conditionReports.length })}
      </Typography>

      {conditionReports.map((report, index) => (
        <Accordion key={report.id} defaultExpanded={index === 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" width="100%">
              <Box sx={{ mr: 2 }}>
                {getStatusIcon(report.status)}
              </Box>
              <Box flexGrow={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {getTranslatedTypeLabel(report.type)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(new Date(report.created_at), 'PPpp', { locale: fr })}
                </Typography>
              </Box>
              <Box>
                <Chip
                  label={report.status}
                  color={getStatusColor(report.status)}
                  size="small"
                  sx={{ mr: 1 }}
                />
                {report.photos && report.photos.length > 0 && (
                  <Chip
                    icon={<PhotoIcon />}
                    label={t('conditionReports.labels.photosCount', { count: report.photos.length })}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              {/* Informations du rapport */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('conditionReports.labels.reportDetails')}
                    </Typography>
                    
                    <Box mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t('conditionReports.labels.type')}:
                      </Typography>
                      <Typography variant="body1">
                        {getTranslatedTypeLabel(report.type)}
                      </Typography>
                    </Box>

                    <Box mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t('conditionReports.labels.createdBy')}:
                      </Typography>
                      <Typography variant="body1">
                        {getUserRoleLabel(report.user_role)} - {report.creator?.name || 'N/A'}
                      </Typography>
                    </Box>

                    <Box mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t('conditionReports.labels.createdAt')}:
                      </Typography>
                      <Typography variant="body1">
                        {format(new Date(report.created_at), 'PPpp', { locale: fr })}
                      </Typography>
                    </Box>

                    {report.notes && (
                      <Box mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          {t('conditionReports.labels.notes')}:
                        </Typography>
                        <Typography variant="body1">
                          {getTranslatedNote(report.notes)}
                        </Typography>
                      </Box>
                    )}

                    {report.refusal_reason && (
                      <Box mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          {t('conditionReports.labels.refusalReason')}:
                        </Typography>
                        <Typography variant="body1" color="error">
                          {report.refusal_reason}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Photos avec carrousel */}
              {report.photos && report.photos.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PhotoIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">
                          {t('conditionReports.labels.photos')}
                        </Typography>
                        <Badge 
                          badgeContent={report.photos.length} 
                          color="primary" 
                          sx={{ ml: 1 }}
                        >
                          <Box />
                        </Badge>
                      </Box>
                      
                      <PhotoCarousel 
                        photos={report.photos} 
                        reportId={report.id}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Dialog pour afficher une photo en grand */}
      <Dialog
        open={!!selectedPhoto}
        onClose={handleClosePhotoDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedPhoto && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {selectedPhoto.photo_type} - {selectedPhoto.description}
                </Typography>
                <IconButton onClick={handleClosePhotoDialog}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <img
                src={selectedPhoto.photo_url}
                alt={selectedPhoto.description}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('conditionReports.labels.type')}:</strong> {selectedPhoto.photo_type}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('conditionReports.labels.size')}:</strong> {(selectedPhoto.file_size / 1024).toFixed(2)} KB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('conditionReports.labels.format')}:</strong> {selectedPhoto.mime_type}
                </Typography>
                {selectedPhoto.original_filename && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('conditionReports.labels.originalName')}:</strong> {selectedPhoto.original_filename}
                  </Typography>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ConditionReportsSection;
